import React from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/blocks/AnimatedSection';
import CommitteeMemberCard from '../../components/blocks/CommitteeMemberCard';
import FacultyAdvisorCard from '../../components/blocks/FacultyAdvisorCard';
import CortexAbstracts2026Publication from '../../components/blocks/CortexAbstracts2026Publication';
import {
    getCortexEventsForListing,
    getCortexEventBySlug,
    getCortex2026KeynoteAdvisorKey,
    CORTEX_2026_KEYNOTE_FOOTER_BADGE,
} from '../../config/cortexEvents';
import { getCommitteeMembersWithRoles, CORTEX_ORGANISING_COMMITTEE } from '../../config/committees';
import { getAdvisorsByKeys, CORTEX_FACULTY_ADVISOR_KEYS } from '../../config/advisors';
import { cortexOutlineButtonClass } from '../../config/themeColors';

const Cortex = () => {
    const events = getCortexEventsForListing();
    const cortex2026KeynoteKey = getCortex2026KeynoteAdvisorKey();
    const conference2026 = getCortexEventBySlug('conference-2026');

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <div className="flex justify-center mb-1.5">
                        <img src="/asset/icons/CORTEX_Logo_Radial.svg" alt="CORTEX Logo" className="h-60 w-auto" />
                    </div>
                    {/* <h1 className="text-4xl font-bold text-gray-900 mb-4">CORTEX</h1> */}
                    <p className="text-xl text-gray-600 max-w-5xl mx-auto text-center mb-6">
                        Conference for Research and Transdisciplinary Experiential Learning - A student-led platform under CCSO, HKU
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <a
                            href="mailto:titanprojectshk@gmail.com"
                            className="inline-block px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:border-orange-400 hover:text-orange-600 transition"
                        >
                            Email Us
                        </a>
                        <a
                            href="https://instagram.com/cortex.hku"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:border-purple-400 hover:text-purple-600 transition"
                        >
                            Follow on Instagram
                        </a>
                        <a
                            href="https://www.linkedin.com/showcase/conference-for-research-and-transdisciplinary-experiential-learning/posts/?feedView=all"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:border-blue-600 hover:text-blue-600 transition"
                        >
                            Follow on LinkedIn
                        </a>
                    </div>
                    <div className="mt-5 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 max-w-3xl mx-auto px-2">
                        <a href="#publications" className={cortexOutlineButtonClass}>
                            Book of Abstracts 2026
                        </a>
                        <Link to="/cortex/keynote-speech-2026" className={cortexOutlineButtonClass}>
                            Keynote Speech 2026 — abstract &amp; bibliography
                        </Link>
                    </div>
                </div>

                {/* What is CORTEX Section */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">What is CORTEX?</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="md:col-span-2">
                                <div className="grid md:grid-cols-1 gap-8 mb-4">
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Vision</h3>
                                        <p className="text-lg text-gray-700 leading-relaxed text-justify mb-4">
                                            The Conference for Research and Transdisciplinary Experiential Learning (CORTEX) is an annual, 
                                            student-led event that aims to foster an academic space for fellow undergraduates to present 
                                            and share their transdisciplinary ideas. Organised as a student society under the University 
                                            of Hong Kong, we provide a platform for talented undergraduates in Hong Kong and beyond a 
                                            chance to present their academic outputs in a formal, academic setting.
                                        </p>
                                        <p className="text-lg text-gray-700 leading-relaxed text-justify">
                                            CORTEX offers a unique opportunity for ambitious undergraduates to receive constructive advice 
                                            on their work from faculty members of a world-class university, as well as a supportive environment 
                                            to connect with other like-minded, bright future scholars.
                                        </p>
                                        <div className="mt-5 bg-gray-50 rounded-lg p-4 transition-colors duration-200 hover:bg-gray-100">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                                Recommended (but not limited to) transdisciplinary sub-themes
                                            </h4>
                                            <ul className="list-disc list-inside space-y-1 text-gray-700">
                                                <li>AI, Big Data and Society</li>
                                                <li>Law, Humanities and Global Challenges</li>
                                                <li>China, Culture and Heritage in a Changing World</li>
                                                <li>Smart cities and Urban Ecologies</li>
                                                <li>Scientific Innovation and Industry in the modern world</li>
                                                <li>Biological and Medical Sciences for Global Good</li>
                                                <li>General transdisciplinary themes</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid md:grid-cols-1 gap-8 mb-4">
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Conference 2026</h3>
                                        <p className="text-lg text-gray-700 leading-relaxed text-justify">
                                            CORTEX 2026 is scheduled to take place at{' '}
                                            {conference2026?.venue || 'Rm 4.35, XR Space, Arts Tech Lab, RRS Tower, Centennial Campus, HKU'} on 4th July, 2026,
                                            with submissions of abstracts currently open until{' '}
                                            {conference2026?.submissionDeadline || '25th June, 2026'}
                                            {conference2026?.deadlineExtended && (
                                                <>
                                                    {' '}
                                                    <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-amber-800 align-middle">
                                                        Extended
                                                    </span>
                                                </>
                                            )}
                                            . For more details, please refer to the information below. We look forward to seeing you this summer!
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="md:col-span-1">
                                <div className="sticky top-6">
                                    <img
                                        src="/asset/images/posters/CORTEX.png"
                                        alt="CORTEX Poster"
                                        className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200"
                                    />
                                </div>
                            </div>
                        </div>
                        {/* <div className="grid md:grid-cols-1 gap-8 mb-6">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Vision</h3>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    To be a leading student-led educational platform in encouraging student endeavors in transdisciplinary research and experiential learning.
                                </p>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Aims</h3>
                            <ol className="list-decimal list-inside space-y-2 text-gray-700">
                                <li>Promote transdisciplinary research and experiential learning within the UG community</li>
                                <li>Provide a platform for UG students to develop and express their ideas and research</li>
                            </ol>
                        </div> */}
                    </div>
                </AnimatedSection>

                {/* Events */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Events</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="space-y-6">
                            {events.map((event) => {
                                const isAmber = event.accentColor === 'amber';
                                const borderCls = isAmber ? 'border-amber-500 hover:bg-amber-50 hover:border-amber-600' : 'border-blue-500 hover:bg-blue-50 hover:border-blue-600';
                                const titleHoverCls = isAmber ? 'group-hover:text-amber-700' : 'group-hover:text-blue-700';
                                const detailBoxCls = isAmber ? 'bg-amber-50 group-hover:bg-amber-100' : 'bg-blue-50 group-hover:bg-blue-100';
                                return (
                                    <Link
                                        key={event.slug}
                                        to={event.path}
                                        className={`group block border-l-4 pl-6 pr-6 rounded-lg transition-all duration-200 hover:shadow-md cursor-pointer ${borderCls}`}
                                    >
                                        {event.cardLayout === 'simple' ? (
                                            <div className={`pt-4 pb-6 ${event.posterImage ? 'grid md:grid-cols-3 gap-10' : ''}`}>
                                                <div className={event.posterImage ? 'md:col-span-2' : ''}>
                                                    <h3 className={`text-xl font-semibold text-gray-900 mt-2 mb-2 transition-colors ${titleHoverCls}`}>{event.title}</h3>
                                                    <p className="text-gray-600 font-semibold mb-2">{event.dateLabel}</p>
                                                    {event.dateLabelSecondary && (
                                                        <p className="text-gray-600 font-semibold mb-2">{event.dateLabelSecondary}</p>
                                                    )}
                                                    <p className="text-gray-700 text-justify mb-4">{event.shortDescription}</p>
                                                    {event.teaser && <p className="text-gray-600 text-sm">{event.teaser}</p>}
                                                </div>
                                                {event.posterImage && (
                                                    <div className="md:col-span-1">
                                                        <div className="mt-2 sticky top-6">
                                                            <img src={encodeURI(event.posterImage)} alt={event.posterAlt || event.title} className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200" />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="grid md:grid-cols-3 gap-10 pt-4 pb-6">
                                                <div className="md:col-span-2">
                                                    <h3 className={`text-xl font-semibold text-gray-900 mt-2 mb-2 transition-colors ${titleHoverCls}`}>{event.title}</h3>
                                                    <div className="mb-4 space-y-1">
                                                        <p className="text-gray-600 font-semibold">{event.dateLabel}</p>
                                                        {event.dateLabelSecondary && <p className="text-gray-600 font-semibold">{event.dateLabelSecondary}</p>}
                                                    </div>
                                                    <p className="text-gray-700 text-justify mb-4">{event.shortDescription}</p>
                                                    {event.longDescription && <p className="text-gray-700 text-justify mb-4">{event.longDescription}</p>}
                                                    {event.details && event.details.length > 0 && (
                                                        <div className="mt-6 space-y-4">
                                                            <div className="grid md:grid-cols-2 gap-4">
                                                                {event.details.map((d, i) => (
                                                                    <div key={i} className={`p-4 rounded-lg transition-colors duration-200 ${detailBoxCls}`}>
                                                                        <h4 className="font-semibold text-gray-900 mb-2">{d.label}</h4>
                                                                        <p className="text-gray-700">{d.value}</p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                {event.posterImage && (
                                                    <div className="md:col-span-1">
                                                        <div className="mt-2 sticky top-6">
                                                            <img src={encodeURI(event.posterImage)} alt={event.posterAlt || event.title} className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200" />
                                                        </div>
                                                    </div>
                                                )}
                                                {event.ctaHref && (
                                                    <div className="md:col-span-3 -mt-4">
                                                        <a
                                                            href={event.ctaHref}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="block w-full text-center px-4 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:border-blue-400 hover:text-blue-600 transition"
                                                        >
                                                            {event.ctaLabel}
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </AnimatedSection>

                <CortexAbstracts2026Publication />

                {/* Organising Committee */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Organising Committee</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed mb-6 text-justify">
                            The Conference for Research and Transdisciplinary Experiential Learning (CORTEX) is organised 
                            by dedicated undergraduate students who are excited to build this platform and connect with 
                            fellow transformative leaders.
                        </p>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {getCommitteeMembersWithRoles(CORTEX_ORGANISING_COMMITTEE).map((member) => (
                                <CommitteeMemberCard key={member.name} {...member} />
                            ))}
                        </div>
                    </div>
                </AnimatedSection>

                {/* Faculty Advisors */}
                <AnimatedSection
                    className="mb-16"
                    heading={
                        <h2 className="text-3xl font-semibold text-gray-900 mb-8">
                            Faculty Advisors
                        </h2>
                    }
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed mb-6 text-justify">
                            To ensure the academic rigour and transdisciplinary initiatives of CORTEX, faculty advisors from
                            the University of Hong Kong and partner institutions provide academic guidance and support to ensure
                            the rigour and quality of CORTEX initiatives.
                        </p>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {CORTEX_FACULTY_ADVISOR_KEYS.map((advisorKey) => {
                                const advisor = getAdvisorsByKeys([advisorKey], 'blue')[0];
                                if (!advisor) return null;
                                const isKeynote = Boolean(cortex2026KeynoteKey && advisorKey === cortex2026KeynoteKey);
                                return (
                                    <FacultyAdvisorCard
                                        key={advisorKey}
                                        {...advisor}
                                        rimHighlight={isKeynote}
                                        footerBadge={isKeynote ? CORTEX_2026_KEYNOTE_FOOTER_BADGE : undefined}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </AnimatedSection>

                {/* Submission Platform
                <section className="mb-16">
                    <h2 className="text-3xl font-semibold text-gray-900 mb-8">Submission Platform</h2>
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="grid md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Research Proposals</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Submit your research proposals through our streamlined platform. Our system supports various research formats
                                    and provides real-time feedback from our expert review panel.
                                </p>
                                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                                    Submit Proposal
                                </button>
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Project Collaborations</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Find and join collaborative research projects. Connect with researchers from different institutions
                                    and contribute to groundbreaking research initiatives.
                                </p>
                                <button className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors">
                                    Browse Projects
                                </button>
                            </div>
                        </div>
                    </div>
                </section> */}

                {/* Templates and Guidelines
                <section className="mb-16">
                    <h2 className="text-3xl font-semibold text-gray-900 mb-8">Templates and Guidelines</h2>
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="p-6 border rounded-lg">
                                <h4 className="font-semibold text-gray-900 mb-3">Research Proposal Template</h4>
                                <p className="text-gray-600 text-sm mb-4">Standardized format for research proposal submissions</p>
                                <button className="text-blue-600 hover:text-blue-800 text-sm">Download Template</button>
                            </div>
                            <div className="p-6 border rounded-lg">
                                <h4 className="font-semibold text-gray-900 mb-3">Collaboration Agreement</h4>
                                <p className="text-gray-600 text-sm mb-4">Framework for multi-institutional research partnerships</p>
                                <button className="text-blue-600 hover:text-blue-800 text-sm">Download Template</button>
                            </div>
                            <div className="p-6 border rounded-lg">
                                <h4 className="font-semibold text-gray-900 mb-3">Publication Guidelines</h4>
                                <p className="text-gray-600 text-sm mb-4">Standards for research publication and dissemination</p>
                                <button className="text-blue-600 hover:text-blue-800 text-sm">Download Guidelines</button>
                            </div>
                            <div className="p-6 border rounded-lg">
                                <h4 className="font-semibold text-gray-900 mb-3">Ethics Review Form</h4>
                                <p className="text-gray-600 text-sm mb-4">Required documentation for research ethics approval</p>
                                <button className="text-blue-600 hover:text-blue-800 text-sm">Download Form</button>
                            </div>
                            <div className="p-6 border rounded-lg">
                                <h4 className="font-semibold text-gray-900 mb-3">Progress Report Template</h4>
                                <p className="text-gray-600 text-sm mb-4">Format for regular project progress updates</p>
                                <button className="text-blue-600 hover:text-blue-800 text-sm">Download Template</button>
                            </div>
                            <div className="p-6 border rounded-lg">
                                <h4 className="font-semibold text-gray-900 mb-3">Final Report Guidelines</h4>
                                <p className="text-gray-600 text-sm mb-4">Requirements for project completion documentation</p>
                                <button className="text-blue-600 hover:text-blue-800 text-sm">Download Guidelines</button>
                            </div>
                        </div>
                    </div>
                </section> */}

                {/* Research Areas
                <section className="mb-16">
                    <h2 className="text-3xl font-semibold text-gray-900 mb-8">Research Areas</h2>
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="grid md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Technology & Innovation</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>• Artificial Intelligence & Machine Learning</li>
                                    <li>• Blockchain & Cryptocurrency</li>
                                    <li>• Internet of Things (IoT)</li>
                                    <li>• Cybersecurity</li>
                                    <li>• Quantum Computing</li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Sustainability & Environment</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>• Renewable Energy Technologies</li>
                                    <li>• Climate Change Mitigation</li>
                                    <li>• Sustainable Agriculture</li>
                                    <li>• Environmental Monitoring</li>
                                    <li>• Green Technology</li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Health & Life Sciences</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>• Biomedical Engineering</li>
                                    <li>• Drug Discovery</li>
                                    <li>• Medical Devices</li>
                                    <li>• Public Health</li>
                                    <li>• Biotechnology</li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Social Sciences & Education</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>• Educational Technology</li>
                                    <li>• Social Innovation</li>
                                    <li>• Digital Humanities</li>
                                    <li>• Policy Research</li>
                                    <li>• Community Development</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section> */}

                {/* Resources
                <section className="mb-16">
                    <h2 className="text-3xl font-semibold text-gray-900 mb-8">Resources</h2>
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="text-center p-6 border rounded-lg">
                                <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                    <span className="text-2xl">💻</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 mb-2">Research Tools</h4>
                                <p className="text-gray-600 text-sm">Access to advanced research software and databases</p>
                            </div>
                            <div className="text-center p-6 border rounded-lg">
                                <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                    <span className="text-2xl">💰</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 mb-2">Funding Opportunities</h4>
                                <p className="text-gray-600 text-sm">Information about grants and funding sources</p>
                            </div>
                            <div className="text-center p-6 border rounded-lg">
                                <div className="w-16 h-16 bg-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                    <span className="text-2xl">🤝</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 mb-2">Mentorship Program</h4>
                                <p className="text-gray-600 text-sm">Connect with experienced researchers and mentors</p>
                            </div>
                        </div>
                    </div>
                </section> */}
            </div>
        </div>
    );
};

export default Cortex;

